import { POLLING_INTERVAL_SECONDS, type TrackingAdapterSummary } from '@chukua/common';
import { Alert, Button, Form, Input, InputNumber } from 'antd';
import type { IntegrationFormValues, useIntegrationSheet } from '../hooks/use-integration-sheet';
import { integrationFormInitialValues } from '../policies/config-form-values.policy';
import { AdapterLogo } from './AdapterLogo';
import { ConfigFields } from './ConfigFields';
import { ConnectionResultAlert } from './ConnectionResultAlert';

interface IntegrationFormProps {
  sheet: ReturnType<typeof useIntegrationSheet>;
  adapter: TrackingAdapterSummary;
  isEdit: boolean;
}

/** The adapter's fields, rendered from its config schema, with the test and save buttons. */
export function IntegrationForm({ sheet, adapter, isEdit }: IntegrationFormProps) {
  return (
    <Form<IntegrationFormValues>
      layout="vertical"
      requiredMark={false}
      disabled={sheet.submitting}
      onFinish={sheet.save}
      initialValues={integrationFormInitialValues(adapter, sheet.integration)}
    >
      <div className="mb-5 flex items-center gap-3">
        <AdapterLogo logoSvg={adapter.logoSvg} name={adapter.displayName} />
        <span className="text-base font-semibold text-fg">{adapter.displayName}</span>
      </div>
      {sheet.error && <Alert className="mb-5" type="error" showIcon title={sheet.error} />}
      <Form.Item
        name="displayName"
        label="Name"
        extra="How this account is listed, e.g. “Mombasa fleet”."
        rules={[{ required: true, message: 'Enter a name' }]}
      >
        <Input />
      </Form.Item>
      <ConfigFields fields={adapter.fields} secretsSet={sheet.integration?.secretsSet ?? []} />
      {adapter.capabilities.includes('polling') && <PollingIntervalField />}
      {sheet.testResult && (
        <div className="mb-5">
          <ConnectionResultAlert result={sheet.testResult} />
        </div>
      )}
      <FormActions sheet={sheet} isEdit={isEdit} />
    </Form>
  );
}

function PollingIntervalField() {
  return (
    <Form.Item
      name="pollingIntervalSeconds"
      label="Polling interval (seconds)"
      extra={`Between ${POLLING_INTERVAL_SECONDS.min} and ${POLLING_INTERVAL_SECONDS.max}.`}
    >
      <InputNumber className="w-full" min={POLLING_INTERVAL_SECONDS.min} max={POLLING_INTERVAL_SECONDS.max} />
    </Form.Item>
  );
}

function FormActions({ sheet, isEdit }: Pick<IntegrationFormProps, 'sheet' | 'isEdit'>) {
  return (
    <Form.Item shouldUpdate noStyle>
      {({ getFieldsValue }) => (
        <div className="flex gap-2 pt-2">
          <Button loading={sheet.testing} onClick={() => sheet.test(getFieldsValue())}>
            Test connection
          </Button>
          <Button type="primary" htmlType="submit" loading={sheet.submitting}>
            {isEdit ? 'Save changes' : 'Install'}
          </Button>
        </div>
      )}
    </Form.Item>
  );
}
